import { useCallback, useState } from "react";

import { inputModelToViewModelConverter, viewModelToInputModelConverter } from "@/components/features/_model/converter";

import { DEFAULT_PRODUCT_CAMPAIGN_INPUT_MODEL } from "./constants/default-input-model";
import { excludeKeys, type ExcludeKeys, type MarketingCampaignFormModel } from "./type";
import { voucherForCampaignInputSchema, type VoucherForCampaignInputSchema } from "./validation-schema";

import type { MarketingCampaignFormModelContextType } from "../ui/_shared/context/context";

type Props = {
	inputModel: MarketingCampaignFormModel["InputModel"];
	queryData: MarketingCampaignFormModel["QueryData"];
};

export const useInitMarketingCampaignFormModelContext = ({
	inputModel: _inputModel,
	queryData,
}: Props): Omit<MarketingCampaignFormModelContextType, "queryData"> & { onResetViewModel: () => void } => {
	const hotelID = _inputModel.hotelID;
	const voucherID = _inputModel.voucherID;

	const defaultInputModel = DEFAULT_PRODUCT_CAMPAIGN_INPUT_MODEL({ voucherID, hotelID, queryData });
	const initInput = _inputModel ?? defaultInputModel;
	const [viewModel, setViewModel] = useState(
		inputModelToViewModelConverter<
			MarketingCampaignFormModel["InputModel"],
			ExcludeKeys,
			MarketingCampaignFormModel["QueryData"],
			VoucherForCampaignInputSchema
		>({
			defaultInput: defaultInputModel,
			initInput,
			currentInput: initInput,
			excludeKeys: excludeKeys,
			zodSchema: voucherForCampaignInputSchema,
		}),
	);

	const onSetViewModel: MarketingCampaignFormModelContextType["onSetViewModel"] = useCallback(
		(values) => {
			const mergedViewModel = {
				...viewModel,
				...values.reduce(
					(acc, cur) => {
						acc[cur.key] = cur.value;
						return acc;
					},
					{} as MarketingCampaignFormModel["ViewModel"],
				),
			} satisfies MarketingCampaignFormModel["ViewModel"];

			const currentInput = viewModelToInputModelConverter<
				MarketingCampaignFormModel["InputModel"],
				ExcludeKeys,
				MarketingCampaignFormModel["QueryData"]
			>(mergedViewModel);

			const newViewModel = inputModelToViewModelConverter<
				MarketingCampaignFormModel["InputModel"],
				ExcludeKeys,
				MarketingCampaignFormModel["QueryData"],
				VoucherForCampaignInputSchema
			>({
				defaultInput: defaultInputModel,
				initInput,
				currentInput,
				excludeKeys: excludeKeys,
				zodSchema: voucherForCampaignInputSchema,
			});

			setViewModel(newViewModel);
		},
		[defaultInputModel, initInput, viewModel],
	);

	const onResetViewModel = useCallback(() => {
		viewModel.isDirty = false;
		viewModel.hasError = false;
	}, [viewModel]);

	return {
		viewModel,
		onSetViewModel,
		onResetViewModel,
	};
};
